// @flow strict
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import {
  BodyMedium,
  ButtonTextSmall,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';

import {
  Table,
  BasicSingleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';

import {useSelector, useDispatch} from 'react-redux';
import {selectJobInformation, selectUserInfo} from 'src/selectors/crm';
import {getJobInformation} from 'src/action-creators/crm';

import css from './skills-tab.css';


export const CustomJobDetails = ({jobId}: {jobId?: string}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const jobInfo = useSelector(selectJobInformation);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (jobId) {
      dispatch(getJobInformation(jobId));
    }
  }, [jobId]);
  const {organization: organizationInfo, user: userInfo} =
    useSelector(selectUserInfo) ?? {};
  const getApiPrefix = (agencyRole: string) => {
    return agencyRole === 'AGENCY' ? 'trmapi/agency' : '/trmapi';
  };
  const apiPrefix = getApiPrefix(userInfo?.roleTitle ?? '');

  const handleFileClick = (fileKey: string) => {
    const fileUrl = `${apiPrefix}/api/attachment-file?fileKey=${encodeURIComponent(
      fileKey,
    )}`;
    window.open(fileUrl, '_blank');
  };

  const BasicTableName = ({data}): React.Node => (
    <BasicSingleCell>
      <BodySmall color="secondary">{data.label}</BodySmall>
    </BasicSingleCell>
  );

  const CustomJobDetail = ({data}): React.Node => {
    const fieldData = data.value;
    console.log('fieldData', fieldData);
    const formatDate = (dateString) => {
      if (!dateString) {
        return null;
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'NaN';
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('en-US', {month: 'long'});
      const year = date.getFullYear();

      return `${day} ${month}, ${year}`;
    };

    if (!fieldData) {
      return (
        <BasicSingleCell>
          <ButtonTextSmall>{'-'}</ButtonTextSmall>
        </BasicSingleCell>
      );
    }

    if (fieldData.fieldType === 'DATE' || fieldData.fieldType === 'DATETIME') {
      const displayValue =
        fieldData.humanReadableValue || formatDate(fieldData.value);
      return (
        <BasicSingleCell>
          {displayValue ? (
            <ButtonTextSmall>{displayValue}</ButtonTextSmall>
          ) : (
            <ButtonTextSmall>{'-'}</ButtonTextSmall>
          )}
        </BasicSingleCell>
      );
    }

    if (!fieldData.humanReadableValue) {
      return (
        <BasicSingleCell>
          <ButtonTextSmall>{'-'}</ButtonTextSmall>
        </BasicSingleCell>
      );
    }

    if (
      fieldData.fieldType === 'MULTIPLE_ATTACHMENT' ||
      fieldData.fieldType === 'SINGLE_ATTACHMENT'
    ) {
      return (
        <BasicSingleCell>
          {fieldData.value && fieldData.value !== 'null' ? (
            fieldData.value.split(',').map((file, index) => {
              const trimmedFile = file.trim();
              if (!trimmedFile) {
                return null;
              }

              // Extract just the filename after the last underscore
              const displayName = trimmedFile.split('__').pop() || trimmedFile;

              return (
                <Chip
                  key={index}
                  iconName="file"
                  onClick={() => handleFileClick(trimmedFile)}
                  semantic="primary"
                  classNames={{wrapper: css.multiUserSelect}}
                >
                  {displayName}
                </Chip>
              );
            })
          ) : (
            <ButtonTextSmall>{'-'}</ButtonTextSmall>
          )}
        </BasicSingleCell>
      );
    } else if (fieldData.fieldType === 'MULTI_USER_SELECT') {
      return (
        <BasicSingleCell>
          {fieldData.humanReadableValue.length > 0 ? (
            fieldData.humanReadableValue.split(',').map((user) => (
              <Chip
                classNames={{wrapper: css.multiUserSelect}}
                semantic="information"
                dismissable={false}
              >
                {user}
              </Chip>
            ))
          ) : (
            <ButtonTextSmall>{'-'}</ButtonTextSmall>
          )}
        </BasicSingleCell>
      );
    } else if (fieldData.fieldType === 'SINGLE_USER_SELECT') {
      return (
        <BasicSingleCell>
          {fieldData.humanReadableValue.length > 0 ? (
            <Chip semantic="information" dismissable={false}>
              {fieldData.humanReadableValue}
            </Chip>
          ) : (
            <ButtonTextSmall>{'-'}</ButtonTextSmall>
          )}
        </BasicSingleCell>
      );
    } else if (fieldData.fieldType === 'NUMBER_RANGE') {
      return (
        <BasicSingleCell>
          {fieldData.humanReadableValue.length > 0 ? (
            <ButtonTextSmall>
              {fieldData.humanReadableValue === 'null - null'
                ? '-'
                : fieldData.humanReadableValue}
            </ButtonTextSmall>
          ) : (
            <ButtonTextSmall>{'-'}</ButtonTextSmall>
          )}
        </BasicSingleCell>
      );
    } else if (fieldData.fieldType === 'CURRENCY_RANGE') {
      return (
        <BasicSingleCell>
          <ButtonTextSmall>
            {fieldData.value === 'NaN,NaN'
              ? t('NaNNaN', 'NaN-NaN')
              : fieldData.humanReadableValue}
          </ButtonTextSmall>
        </BasicSingleCell>
      );
    } else {
      return (
        <BasicSingleCell>
          <ButtonTextSmall>
            {fieldData.humanReadableValue?.length > 0 &&
            fieldData.humanReadableValue !== 'null'
              ? fieldData.humanReadableValue
              : '-'}
          </ButtonTextSmall>
        </BasicSingleCell>
      );
    }
  };

  const tableEntries =
    jobInfo?.customFields?.map((field) => ({
      label: field.name,
      value: field,
      id: field.name,
    })) || [];

  return (
    <Table
      borderRadius="12px"
      entries={tableEntries}
      headers={[
        {
          label: t('Job_Attribute', 'Job Attribute'),
          key: 'label',
          sortable: false,
          className: css.smallColumn,
          render: BasicTableName,
        },
        {
          label: t('Details', 'Details'),
          key: 'value',
          sortable: false,
          render: CustomJobDetail,
        },
      ]}
    />
  );
};
